
import React, { useCallback, useEffect, useRef } from 'react';
import { useAdminPage } from '@/hooks/useAdminPage';
import AdminLayout from '@/components/admin/AdminLayout';
import AdminContent from '@/components/admin/AdminContent';
import ConfirmAttendanceDialog from '@/components/admin/ConfirmAttendanceDialog';
import { useAttendanceEvents } from '@/hooks/admin/useAttendanceEvents';
import FloatingActionButton from '@/components/admin/FloatingActionButton';
import { QrScanner } from '@/components/admin/scanner';

const AdminPage = () => {
  const {
    selectedAttendee,
    isDialogOpen,
    setIsDialogOpen,
    isConfirming,
    confirmAttendance,
    refreshAttendees,
    silentlyUpdateAttendee,
    updateAttendee,
    handleQrScan,
    handleCodeSubmit
  } = useAdminPage();
  
  const { dispatchUpdateEvent } = useAttendanceEvents();
  const lastRefreshTime = useRef(Date.now());
  const isRefreshing = useRef(false);
  
  const handleOpenQrScanner = useCallback(() => {
    console.log('Attempting to open QR Scanner camera - NO PAGE REFRESH');
    
    const cameraButton = document.querySelector('button[data-qr-scanner-button="true"]');
    if (cameraButton && cameraButton instanceof HTMLElement) {
      console.log('Camera button found by data attribute, clicking it - NO PAGE REFRESH');
      cameraButton.click();
      return;
    }
    
    const scanner = document.querySelector('[data-qr-scanner="true"]');
    if (scanner) {
      console.log('QR Scanner found in DOM - NO PAGE REFRESH');
      const buttons = scanner.querySelectorAll('button');
      if (buttons && buttons.length > 0 && buttons[0] instanceof HTMLElement) {
        console.log('First button in scanner found, clicking it - NO PAGE REFRESH');
        buttons[0].click();
        return;
      }
    }
    
    const allCameraButtons = document.querySelectorAll('button svg[class*="lucide-camera"]');
    if (allCameraButtons && allCameraButtons.length > 0) {
      console.log('Found button with camera icon, attempting to click parent - NO PAGE REFRESH');
      const parentButton = allCameraButtons[0].closest('button');
      if (parentButton && parentButton instanceof HTMLElement) {
        console.log('Clicking camera icon parent button - NO PAGE REFRESH');
        parentButton.click();
        return;
      }
    }
    
    console.error('Could not find any way to open the QR scanner');
  }, []);
  
  const throttledRefresh = useCallback((options = { force: false }) => {
    const now = Date.now();
    const minInterval = 5000;
    
    if (isRefreshing.current && !options.force) return;
    
    if (options.force || now - lastRefreshTime.current > minInterval) {
      console.log('Throttled silent refresh starting - NO PAGE REFRESH');
      isRefreshing.current = true;
      lastRefreshTime.current = now;
      
      refreshAttendees()
        .then(() => {
          console.log('AdminPage: Silent refresh complete - NO PAGE REFRESH');
        })
        .catch(error => {
          console.error('AdminPage: Refresh error:', error);
        })
        .finally(() => {
          isRefreshing.current = false;
        });
    }
  }, [refreshAttendees]);
  
  const handleDirectUpdate = useCallback(async (code: string, updates: any) => {
    try {
      console.log(`Direct update for ${code} - NO PAGE REFRESH`);
      const result = await updateAttendee(code, {
        ...updates,
        t_shirt_size: updates.t_shirt_size
      });
      
      if (result) {
        console.log(`Silently updating attendee after direct update - NO PAGE REFRESH`);
        silentlyUpdateAttendee(result);
        dispatchUpdateEvent(result, result.checkedInAt || new Date().toISOString());
        return result;
      }
      return null;
    } catch (error) {
      console.error('Error in direct update:', error);
      setTimeout(() => throttledRefresh({ force: true }), 3000);
      return null;
    }
  }, [updateAttendee, silentlyUpdateAttendee, dispatchUpdateEvent, throttledRefresh]);
  
  const handleConfirmAttendance = useCallback(async () => {
    if (!selectedAttendee) return false;
    
    try {
      console.log(`Confirming attendance for ${selectedAttendee.name} - NO PAGE REFRESH`);
      const attendeeWithUpdates = {
        ...selectedAttendee,
        attended: true,
        checkedInAt: new Date().toISOString()
      };
      
      // Optimistically update UI before actual confirmation
      silentlyUpdateAttendee(attendeeWithUpdates);
      
      const result = await confirmAttendance();
      
      if (result && selectedAttendee) {
        console.log(`Attendance confirmation successful - NO PAGE REFRESH`);
        dispatchUpdateEvent(attendeeWithUpdates, attendeeWithUpdates.checkedInAt);
      }
      
      return true;
    } catch (error) {
      console.error('Error during confirmation:', error);
      setTimeout(() => throttledRefresh({ force: true }), 3000);
      return false;
    }
  }, [selectedAttendee, silentlyUpdateAttendee, confirmAttendance, dispatchUpdateEvent, throttledRefresh]);

  return (
    <AdminLayout>
      <AdminContent 
        onRefreshData={() => throttledRefresh({ force: true })}
        onDirectUpdate={handleDirectUpdate}
      />
      
      <ConfirmAttendanceDialog 
        isOpen={isDialogOpen} 
        setIsOpen={setIsDialogOpen} 
        attendee={selectedAttendee} 
        onConfirm={handleConfirmAttendance}
        isConfirming={isConfirming}
      />
      
      <FloatingActionButton onOpenQrScanner={handleOpenQrScanner} />
    </AdminLayout>
  );
};

export default React.memo(AdminPage);
